import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { Sparkles, Zap, Compass, Upload, X, ArrowRight, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STAGES = ["Understanding the activity", "Reusing your company profile", "Identifying hazards & high-risk work", "Structuring sections & risk tables", "Checking references against the UK library", "Flagging assumptions & missing information"];
const EXAMPLES = ["Two engineers attending a commercial warehouse to replace defective high-level lighting using a scissor lift.", "Create RAMS for two maintenance engineers replacing a leaking water cylinder in an occupied residential apartment block.", "Toolbox talk on ladder safety for new starters."];

const Generating = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => Math.min(x + 1, STAGES.length - 1)), 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mx-auto max-w-xl py-16 text-center" data-testid="generating-state">
      <div className="shimmer mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-health/10"><Sparkles className="h-7 w-7 text-health" /></div>
      <h2 className="mt-6 text-2xl font-semibold">Saflr is drafting your document</h2>
      <p className="mt-1 text-sm text-muted-foreground">This usually takes 30–90 seconds. Credits are only used if it succeeds.</p>
      <ul className="mx-auto mt-8 max-w-sm space-y-2 text-left text-sm">
        {STAGES.map((s, k) => <li key={s} className={`flex items-center gap-2 transition-opacity ${k <= i ? "opacity-100" : "opacity-30"}`}><span className={`h-2 w-2 rounded-full ${k < i ? "bg-health" : k === i ? "animate-pulse bg-[#FFA000]" : "bg-muted-foreground"}`} />{s}</li>)}
      </ul>
    </div>
  );
};

export default function Generate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fileRef = useRef();
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(params.get("cat") || "");
  const [sub, setSub] = useState("");
  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("guided");
  const [meta, setMeta] = useState({ project_ref: "", site_address: "" });
  const [refs, setRefs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("input");
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  const category = cats.find((c) => c.slug === (analysis?.category_slug || cat));
  const cost = category ? category.credit_cost + (mode === "guided" ? 1 : 0) : null;

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/references", fd);
      setRefs([...refs, data]);
      toast.success(`${data.filename} added as reference`);
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };
  const generate = async (a) => {
    setPhase("generating");
    try {
      const { data } = await api.post("/ai/generate", { prompt, mode, category_slug: a.category_slug, subcategory: a.subcategory, answers, ...meta, reference_ids: refs.map((r) => r.id), client_request_id: crypto.randomUUID() });
      const job = await pollJob(data.job_id);
      toast.success("Draft created — review before approval");
      nav(`/app/documents/${job.document_id}`);
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
      setPhase(analysis ? "questions" : "input");
    }
  };
  const start = async () => {
    if (prompt.trim().length < 5) return toast.error("Describe the job or document you need");
    if (mode === "quick" && cat && sub) return generate({ category_slug: cat, subcategory: sub });
    setPhase("analysing");
    try {
      const { data } = await api.post("/ai/analyse", { prompt, category_slug: cat || null, subcategory: sub || null, reference_ids: refs.map((r) => r.id) });
      setAnalysis(data);
      if (mode === "quick") return generate(data);
      setPhase("questions");
    } catch (err) {
      toast.error(errMsg(err));
      setPhase("input");
    }
  };

  if (phase === "generating") return <Generating />;
  if (phase === "analysing") return <div className="py-24 text-center" data-testid="analysing-state"><div className="shimmer mx-auto h-14 w-14 rounded-2xl bg-health/10" /><p className="mt-6 font-medium">Analysing your request…</p><p className="text-sm text-muted-foreground">Working out exactly what Saflr needs to ask.</p></div>;

  if (phase === "questions") return (
    <div className="mx-auto max-w-4xl space-y-6 fade-up" data-testid="clarify-step">
      <div><p className="eyebrow">Guided Professional · Step 2 of 3</p><h1 className="mt-2 text-3xl font-bold">{analysis.title}</h1><p className="mt-2 text-muted-foreground">{analysis.activity_summary}</p></div>
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border bg-card p-5"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Document type</p><p className="mt-2 font-medium">{analysis.category_name}</p><p className="text-sm text-muted-foreground">{analysis.subcategory}</p></div>
        <div className="rounded-2xl border bg-card p-5 md:col-span-2"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Already known from your profile</p><ul className="mt-2 space-y-1 text-sm">{(analysis.known_from_profile || []).map((k) => <li key={k} className="flex gap-2"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-health" />{k}</li>)}</ul></div>
      </div>
      {analysis.high_risk_activities?.length > 0 && (
        <div className="rounded-2xl border border-alert/30 bg-alert/5 p-5" data-testid="high-risk-panel"><p className="flex items-center gap-2 text-sm font-semibold text-alert"><AlertTriangle className="h-4 w-4" />High-risk activities identified — competent review required</p><div className="mt-2 flex flex-wrap gap-2">{analysis.high_risk_activities.map((h) => <span key={h} className="rounded-full bg-alert/10 px-3 py-1 text-xs">{h}</span>)}</div></div>
      )}
      <div className="rounded-2xl border bg-card p-6">
        <h2 className="text-lg font-semibold">A few questions to make this site-specific</h2>
        <p className="text-sm text-muted-foreground">Leave blank anything you don't know — Saflr will mark it [TO BE CONFIRMED].</p>
        <div className="mt-6 space-y-5">
          {(analysis.questions || []).map((q) => (
            <div key={q.id} className="space-y-1.5">
              <Label>{q.question}</Label><p className="text-xs text-muted-foreground">{q.why}</p>
              {q.type === "yesno" ? (
                <div className="flex gap-2">{["Yes", "No", "Unknown"].map((o) => <button key={o} type="button" data-testid={`answer-${q.id}-${o.toLowerCase()}`} onClick={() => setAnswers({ ...answers, [q.question]: o })} className={`rounded-full border px-4 py-1.5 text-sm ${answers[q.question] === o ? "border-health bg-health/10" : ""}`}>{o}</button>)}</div>
              ) : <Input data-testid={`answer-${q.id}`} value={answers[q.question] || ""} onChange={(e) => setAnswers({ ...answers, [q.question]: e.target.value })} />}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setPhase("input")}>Back</Button>
        <Button onClick={() => generate(analysis)} className="rounded-full px-6" data-testid="generate-document-btn">Generate document · {cost} credits<ArrowRight className="ml-1 h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl space-y-8 fade-up" data-testid="generate-page">
      <div><p className="eyebrow">Saflr AI document engine</p><h1 className="mt-2 text-3xl font-bold sm:text-4xl">Describe the job. Let Saflr do the paperwork.</h1></div>
      <div className="rounded-3xl border bg-card p-6 shadow-sm">
        <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Two engineers attending a commercial warehouse to replace defective high-level lighting using a scissor lift." className="resize-none border-0 p-0 text-base shadow-none focus-visible:ring-0" data-testid="generate-prompt" />
        <div className="mt-3 flex flex-wrap gap-2">{EXAMPLES.map((e) => <button key={e} onClick={() => setPrompt(e)} className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground hover:text-foreground">{e.slice(0, 48)}…</button>)}</div>
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t pt-4">
          <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.csv" hidden onChange={upload} data-testid="reference-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current.click()} data-testid="upload-reference-btn"><Upload className="mr-1.5 h-4 w-4" />Reference file (SDS, scope)</Button>
          {refs.map((r) => <span key={r.id} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs">{r.filename}<button onClick={() => setRefs(refs.filter((x) => x.id !== r.id))}><X className="h-3 w-3" /></button></span>)}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {[["guided", Compass, "Guided Professional", "Saflr asks targeted questions first for a more detailed, site-specific document."], ["quick", Zap, "Quick Draft", "Generate immediately from limited information. Assumptions and gaps are clearly flagged."]].map(([m, Icon, t, d]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`} className={`card-lift rounded-2xl border p-5 text-left ${mode === m ? "border-health ring-1 ring-health" : ""}`}>
            <Icon className={`h-5 w-5 ${mode === m ? "text-health" : "text-muted-foreground"}`} /><p className="mt-3 font-semibold">{t}</p><p className="text-sm text-muted-foreground">{d}</p>
          </button>
        ))}
      </div>
      <div className="rounded-2xl border bg-card p-6">
        <h2 className="font-semibold">Document type <span className="text-sm font-normal text-muted-foreground">(optional — Saflr can work it out)</span></h2>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {cats.map((c) => { const Icon = Icons[c.icon] || Icons.FileText; return (
            <button key={c.slug} data-testid={`cat-${c.slug}`} onClick={() => { setCat(cat === c.slug ? "" : c.slug); setSub(""); }} className={`flex flex-col items-start gap-2 rounded-xl border p-3 text-left text-xs font-medium transition-colors ${cat === c.slug ? "border-health bg-health/5" : "hover:bg-accent"}`}><Icon className="h-4 w-4" />{c.name}</button>
          ); })}
        </div>
        {category && (
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Specific document</Label>
              <Select value={sub} onValueChange={setSub}><SelectTrigger data-testid="subcategory-select"><SelectValue placeholder="Choose…" /></SelectTrigger><SelectContent>{category.subcategories.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Project reference</Label><Input value={meta.project_ref} onChange={(e) => setMeta({ ...meta, project_ref: e.target.value })} data-testid="project-ref-input" /></div>
            <div className="space-y-1.5"><Label>Site address</Label><Input value={meta.site_address} onChange={(e) => setMeta({ ...meta, site_address: e.target.value })} data-testid="site-address-input" /></div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground" data-testid="credit-estimate">{cost ? `Estimated cost: ${cost} credits` : "Credit cost shown once the document type is known"} · charged only on success</p>
        <Button onClick={start} size="lg" className="rounded-full px-8" data-testid="start-generation-btn"><Sparkles className="mr-2 h-4 w-4" />{mode === "guided" ? "Continue" : "Generate Quick Draft"}</Button>
      </div>
    </div>
  );
}
